<template>
  <v-card flat>
    <v-card-title>
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        label="Search Payment History"
        single-line
        hide-details
        outlined
        dense
        class="mr-4 mb-2 mb-sm-0"
        style="max-width: 300px"
        clearable
        @input="onSearch"
      ></v-text-field>
    </v-card-title>
    <v-card-text>
      <!-- Loading State -->
      <v-row
        v-if="loading && history.length === 0"
        justify="center"
        class="py-10"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-row>

      <v-alert
        v-if="!loading && history.length === 0"
        type="info"
        text
        class="mt-4"
      >
        You haven't made any generic payments yet.
      </v-alert>

      <!-- List View -->
      <v-data-table
        v-if="history.length > 0"
        :headers="headers"
        :items="history"
        :loading="loading"
        hide-default-footer
        class="elevation-0 border-top mt-2"
      >
        <template v-slot:item.amount="{ item }">
          <span class="font-weight-bold">{{ item.amount | currency }}</span>
        </template>
        <template v-slot:item.paidDate="{ item }">
          {{ formatDate(item.paidDate) }}
        </template>
        <template v-slot:item.status>
          <v-chip color="success" x-small outlined label>
            <v-icon left x-small>mdi-check-circle</v-icon>
            Paid
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center">
            <v-btn
              small
              icon
              color="primary"
              @click="viewDetails(item)"
              title="View Details"
            >
              <v-icon small>mdi-eye-outline</v-icon>
            </v-btn>
            <v-btn
              v-if="item.postedReceiptNo"
              icon
              small
              color="secondary"
              title="Download Receipt"
              @click="
                $router.push({
                  name: 'receipt',
                  params: { code: encodeRoute(item.postedReceiptNo) },
                })
              "
            >
              <v-icon small>mdi-download-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Load More -->
      <div v-if="history.length > 0" class="text-center py-6">
        <v-btn
          v-if="history.length < pagination.total"
          color="primary"
          outlined
          :loading="loading"
          @click="loadMore"
        >
          <v-icon left>mdi-chevron-down</v-icon>
          Show More History ({{ history.length }} of {{ pagination.total }})
        </v-btn>
        <div v-else class="caption grey--text">
          Showing all {{ pagination.total }} records
        </div>
      </div>

      <v-navigation-drawer
        v-model="detailsDrawer"
        fixed
        right
        temporary
        width="450"
        overlay-color="primary"
        overlay-opacity="0.1"
      >
        <v-card flat height="100%" class="d-flex flex-column">
          <v-toolbar flat color="primary" dark>
            <v-toolbar-title class="subtitle-1"
              >Payment Details</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn icon @click="detailsDrawer = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-toolbar>

          <v-card-text class="flex-grow-1 pa-0">
            <v-list two-line subheader>
              <v-subheader>Transaction Information</v-subheader>
              <v-list-item>
                <v-list-item-avatar color="primary lighten-5">
                  <v-icon color="primary">mdi-file-document-outline</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-subtitle>Document No</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-bold">{{
                    selectedItem ? selectedItem.no : ""
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-avatar color="primary lighten-5">
                  <v-icon color="primary">mdi-cog-outline</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-subtitle>Service</v-list-item-subtitle>
                  <v-list-item-title>{{
                    selectedItem ? selectedItem.serviceDescription : ""
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>
              <v-subheader>Payment Summary</v-subheader>

              <v-list-item>
                <v-list-item-avatar color="success lighten-5">
                  <v-icon color="success">mdi-cash-check</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-subtitle>Amount Paid</v-list-item-subtitle>
                  <v-list-item-title
                    class="headline success--text font-weight-bold"
                  >
                    {{ selectedItem ? selectedItem.amount : 0 | currency }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item v-if="selectedItem && selectedItem.postedReceiptNo">
                <v-list-item-avatar color="grey lighten-4">
                  <v-icon color="grey darken-2">mdi-receipt</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-subtitle>Receipt Number</v-list-item-subtitle>
                  <v-list-item-title>{{
                    selectedItem.postedReceiptNo
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>
              <v-subheader>Timeline</v-subheader>

              <v-list-item>
                <v-list-item-avatar color="grey lighten-4">
                  <v-icon color="grey darken-2">mdi-calendar-check</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-subtitle>Payment Date</v-list-item-subtitle>
                  <v-list-item-title>{{
                    selectedItem ? formatDate(selectedItem.paidDate) : ""
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions
            class="pa-6"
            v-if="selectedItem && selectedItem.postedReceiptNo"
          >
            <v-btn
              color="secondary"
              block
              outlined
              @click="
                $router.push({
                  name: 'receipt',
                  params: { code: encodeRoute(selectedItem.postedReceiptNo) },
                })
              "
            >
              Download Official Receipt
              <v-icon right>mdi-download</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>
    </v-card-text>
  </v-card>
</template>

<script>
import DateMixin from "@/mixins/DateMixin";
import RouterMixin from "@/router/RouterMixin";
import { AuthService } from "../../auth";

export default {
  name: "PaymentHistory",
  mixins: [DateMixin, RouterMixin],
  data: () => ({
    detailsDrawer: false,
    selectedItem: null,
    search: "",
    searchTimeout: null,
    headers: [
      { text: "No", value: "no" },
      { text: "Service", value: "serviceDescription" },
      { text: "Payment Date", value: "paidDate" },
      { text: "Amount", value: "amount" },
      { text: "Status", value: "status" },
      { text: "Actions", value: "actions", sortable: false },
    ],
  }),
  computed: {
    history() {
      return this.$store.getters["Payments/history"];
    },
    loading() {
      return this.$store.getters["Payments/loading"];
    },
    pagination() {
      return this.$store.getters["Payments/pagination"].history;
    },
    profile() {
      return AuthService.user?.profile || {};
    },
    page: {
      get() {
        return this.pagination.page;
      },
      set(val) {
        this.$store.commit("Payments/SET_PAGINATION", {
          type: "history",
          data: { page: val },
        });
      },
    },
    pageCount() {
      return (
        Math.ceil(this.pagination.total / this.pagination.itemsPerPage) || 1
      );
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    onSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.fetchData();
      }, 500);
    },
    fetchData() {
      const skip = (this.page - 1) * this.pagination.itemsPerPage;
      const top = this.pagination.itemsPerPage;
      const where = JSON.stringify({
        profileID: this.profile.ProfileID,
        // status: "Released",
        paid: true,
        // documentType: "Invoice",
      });
      this.$store.dispatch("Payments/getHistory", {
        $skip: skip,
        $top: top,
        $filter: where,
        search: this.search,
      });
    },
    onPageChange() {
      this.fetchData();
    },
    loadMore() {
      const nextPage = this.page + 1;
      this.page = nextPage;
      const skip = (nextPage - 1) * this.pagination.itemsPerPage;
      const top = this.pagination.itemsPerPage;
      this.$store.dispatch("Payments/loadMoreHistory", {
        ...(this.pagination.nextCursor
          ? { cursor: this.pagination.nextCursor }
          : {
              $skip: skip,
              $top: top,
              search: this.search,
            }),
      });
    },
    viewDetails(item) {
      this.selectedItem = item;
      this.detailsDrawer = true;
    },
  },
};
</script>
